import {
  createContext,
  useState,
  PropsWithChildren,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import { Logger, useMutation, sendTeaLog } from '@features/libs';
import { merge } from 'lodash-es';
import tw from 'twin.macro';
import { Button, Card, Modal } from '@m4b-design/components';
import { I18nText } from '@features/m4b-components-new';
import { ecLocalStorage as submoduleEcLocalStorage } from '@ecom-alliance/libs';
import { useLocation } from '@jupiter/plugin-runtime/router';
import styled from '@jupiter/plugin-runtime/styled';
import NotificationIcon from './notification.svg';
import {
  CreatorConnect,
  Quota,
  QuotaType,
} from '../../api/affiliate/data/quota.d';
import {
  AffiliateSellerCreatorConnectCheckResponse,
  AffiliateSellerCreatorConnectCheckResponseData,
  AffiliateSellerQuotaInfoResponse,
  AffiliateSellerQuotaInfoResponseData,
} from '../../api/affiliate/serv/oec_affiliate_invitation_api.d';
import { SellerPaths } from '../../paths';

export type ApiMutationService<T, P> = {
  run: (props: P) => Promise<T | undefined>;
  isLoading?: boolean;
  isSuccess?: boolean;
};

/** @returns */
export interface IConnectionQuotaState {
  quotaInfo?: AffiliateSellerQuotaInfoResponseData;
  creatorConnectionMap: Record<string, CreatorConnect>;
  // refresh quota in context and return new data
  refreshQuota: ApiMutationService<AffiliateSellerQuotaInfoResponseData, void>;
  // batch check creators connection state instant and return new data
  checkCreatorsConnection: ApiMutationService<
    AffiliateSellerCreatorConnectCheckResponseData,
    string[]
  >;
  hasFlatFee?: boolean;
  isHitGrayScale?: boolean;
  isLoadingGrayscaleStatus: boolean;
  shopRegion?: string;
  account?: IAccountInfo;
  platformConfig?: Record<string, unknown>;
  updateQuotaInfoFromQuota: (quota?: Quota) => void;
  updateCreatorConnectMap: (list: (CreatorConnect | undefined)[]) => void;
  // updateCreatorIMStatusMap: (data: CRMCreatorIM) => void;
  checkReachedQuotaNumberLimited: (oecIds: string[]) => boolean;
}

const DEFAULT_VALUE: IConnectionQuotaState = {} as any;

const ConnectionQuotaContext = createContext(DEFAULT_VALUE);

/** @props */
type ConnectionQuotaContextProviderProps = PropsWithChildren<{
  isHitGrayScale?: boolean;
  isLoadingGrayscaleStatus: boolean;
  account?: IAccountInfo;
  hasFlatFee?: boolean;
  platformConfig?: Record<string, unknown>;
  ecLocalStorage: typeof submoduleEcLocalStorage;
  sendTeaLog: typeof sendTeaLog;
  fetchQuotaData: () => Promise<AffiliateSellerQuotaInfoResponse>;
  checkCreatorConnection: (
    creatorOecIdList: string[],
  ) => Promise<AffiliateSellerCreatorConnectCheckResponse>;
}>;

const STARTER_PACK_USED_UP_KEY = 'quota-starter-pack-used-up';

const ROUTE_PAGE_NAME_MAP = {
  [SellerPaths.OPEN_COLLABORATION_LIST]: 'affiliate_open_collaboration',
  [SellerPaths.CREATOR_MANAGEMENT]: 'affiliate_crm',
  [SellerPaths.TARGET_INVITATION_LIST]: 'target_invitation_list',
  [SellerPaths.TARGET_INVITATION_CAMPAIGN_CREATE]: 'target_invitation_create',
  [SellerPaths.TARGET_INVITATION_EDIT]: 'target_invitation_edit',
  [SellerPaths.CREATOR_MARKETPLACE]: 'affiliate_find_creators',
  [SellerPaths.CREATOR_DETAIL]: 'affiliate_creator_details',
  '/seller/im': 'seller_im',
};

const StyledCard = styled(Card)`
  padding: 0px !important;
  &.m4b-card-direction-vertical > .m4b-card-icon {
    ${tw`bg-neutral-bg2 rounded-8 p-30 mb-24`}
  }
  .m4b-card-box-info {
    ${tw`px-36`}
  }
`;

export const ConnectionQuotaContextProvider = (
  props: ConnectionQuotaContextProviderProps,
) => {
  const {
    account,
    platformConfig,
    hasFlatFee,
    isHitGrayScale,
    isLoadingGrayscaleStatus,
    ecLocalStorage,
    children,
    sendTeaLog,
    fetchQuotaData,
    checkCreatorConnection,
  } = props;
  const [quotaInfo, setQuotaInfo] =
    useState<AffiliateSellerQuotaInfoResponseData>();
  const [creatorConnectionMap, setCreatorConnectionMap] = useState<
    Record<string, CreatorConnect>
  >({});
  const location = useLocation();

  useEffect(() => {
    if (!isHitGrayScale) {
      return;
    }
    fetchQuotaDataMutation.mutateAsync();
  }, [isHitGrayScale, location.pathname]);

  const fetchQuotaDataMutation = useMutation(
    async () => {
      const { data } = await fetchQuotaData();
      return data;
    },
    {
      onSuccess(data) {
        if (!data) {
          return;
        }
        setQuotaInfo(data);
        showStarterPackUsedUpModal(data?.quota);
      },
      onError(err) {
        Logger.error('fetchQuotaData Error', '', err as any);
      },
    },
  );

  const checkCreatorsConnectionMutation = useMutation(
    async (_creatorIdList: string[]) => {
      const filteredCreatorList = _creatorIdList.filter(Boolean);
      if (filteredCreatorList.length) {
        const { data } = await checkCreatorConnection(_creatorIdList);
        return data;
      } else {
        return {};
      }
    },
    {
      onSuccess(data) {
        const { creator_connects = [], quota } = data || {};
        updateCreatorConnectMap(creator_connects);
        updateQuotaInfoFromQuota(quota);
      },
      onError(err) {
        Logger.error('checkCreatorConnection Error', '', err as any);
      },
    },
  );

  const checkReachedQuotaNumberLimited = useCallback(
    (oecIds: string[]) => {
      if (!isHitGrayScale || isLoadingGrayscaleStatus || !oecIds.length) {
        return false;
      }

      const { used_num = '0', max_quota = '-1' } = quotaInfo?.quota || {};
      const usedNumber = Number(used_num);
      const maxNumber = Number(max_quota);
      if (quotaInfo?.quota === undefined) {
        return false;
      }

      if (maxNumber === -1) {
        return false;
      }

      let requiredNumber = 0;
      oecIds.forEach(oecId => {
        const { is_connect, is_pair_connect } =
          creatorConnectionMap[oecId] || {};

        if (!is_connect && !is_pair_connect) {
          ++requiredNumber;
        }
      });

      return requiredNumber > Math.max(maxNumber - usedNumber, 0);
    },
    [quotaInfo, creatorConnectionMap, isHitGrayScale, isLoadingGrayscaleStatus],
  );

  const updateQuotaInfoFromQuota = (quota?: Quota) => {
    if (!quota) {
      return;
    }
    showStarterPackUsedUpModal(quota);
    setQuotaInfo(preQuotaInfo => ({ ...(preQuotaInfo || {}), quota }));
  };

  const updateCreatorConnectMap = (
    creatorConnectList?: (CreatorConnect | undefined)[],
  ) => {
    const filteredList = creatorConnectList?.filter(item =>
      Boolean(item?.creator_id),
    );

    if (!filteredList?.length) {
      return;
    }

    const connectMap = filteredList.reduce(
      (pre, cur) => ({
        ...pre,
        [cur?.creator_id as string]: cur,
      }),
      {},
    );
    setCreatorConnectionMap(preState => merge({ ...preState }, connectMap));
  };

  const showStarterPackUsedUpModal = (newQuota?: Quota) => {
    // marked by shop id
    const uniqueId = `${STARTER_PACK_USED_UP_KEY}-${
      account?.currentShopInfo?.shop_id || ''
    }`;
    const isShowed = ecLocalStorage.getItem(uniqueId);

    const isMatchedPopupCondition =
      isHitGrayScale &&
      quotaInfo?.quota?.quota_type === QuotaType.Bonus &&
      newQuota?.quota_type &&
      newQuota.quota_type !== QuotaType.Bonus;

    if (!isShowed && isMatchedPopupCondition) {
      const commonParams = {
        page_name: ROUTE_PAGE_NAME_MAP[window.location.pathname] || '',
        type: 'popup',
        popup_name: 'starter_pack_use_up',
      };
      sendTeaLog('affiliate_page_show', commonParams);
      const modalInstance = Modal.info({
        title: null,
        icon: false,
        content: (
          <StyledCard
            shadow={false}
            className="text-center"
            icon={<img src={NotificationIcon} height={120} width={120} />}
            title={
              <I18nText
                i18nKey="affiliate_taco_starter_pack_use_up_popup_title"
                defaultText="Starter pack is used up"
              />
            }
            direction="vertical">
            <div className="mt-12 text-neutral-text2 text-body-l-regular">
              <I18nText
                i18nKey="affiliate_taco_starter_pack_use_up_popup_content"
                defaultText="You've reached out to all the creators in your starter pack. Now, GMV-based weekly limits will apply to further outreach"
              />
            </div>
          </StyledCard>
        ),
        onCancel: () =>
          sendTeaLog('affiliate_button_click', {
            ...commonParams,
            click_for: 'close',
          }),
        footer: (
          <div className="flex justify-center mb-8">
            <Button
              type="primary"
              size="large"
              onClick={() => {
                modalInstance.close();
                sendTeaLog('affiliate_button_click', {
                  ...commonParams,
                  click_for: 'check_new_rules',
                });
                window.open(
                  `${SellerPaths.QUOTA_RULE_OVERVIEW}${
                    commonParams.page_name ? `?${commonParams.page_name}` : ''
                  }`,
                );
              }}>
              <I18nText
                i18nKey="affiliate_taco_starter_pack_use_up_popup_button"
                defaultText="Check outreach rules"
              />
            </Button>
          </div>
        ),
        afterClose() {
          ecLocalStorage.setItem(uniqueId, '1');
        },
      });
    }
  };

  return (
    <ConnectionQuotaContext.Provider
      value={{
        quotaInfo,
        account,
        hasFlatFee,
        isHitGrayScale,
        isLoadingGrayscaleStatus,
        creatorConnectionMap,
        refreshQuota: {
          run: fetchQuotaDataMutation.mutateAsync,
          isLoading: fetchQuotaDataMutation.isLoading,
          isSuccess: fetchQuotaDataMutation.isSuccess,
        },
        checkCreatorsConnection: {
          run: checkCreatorsConnectionMutation.mutateAsync,
          isLoading: checkCreatorsConnectionMutation.isLoading,
          isSuccess: checkCreatorsConnectionMutation.isSuccess,
        },
        shopRegion: account?.currentShopInfo?.shop_name,
        platformConfig,
        updateCreatorConnectMap,
        updateQuotaInfoFromQuota,
        checkReachedQuotaNumberLimited,
      }}>
      {children}
    </ConnectionQuotaContext.Provider>
  );
};

export const useConnectionQuota = () => useContext(ConnectionQuotaContext);
